import React, { useState, useEffect } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { getBirds, Bird } from '../../api/birds';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface HealthRemindersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reminderData: any) => Promise<void>;
  birds?: Bird[];
}

const HealthRemindersModal: React.FC<HealthRemindersModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  birds = [],
}) => {
  const [formData, setFormData] = useState({
    birdId: '',
    reminderType: 'HEALTH_CHECKUP',
    reminderDate: '',
    frequency: 'ONCE',
    title: '',
    description: '',
    priority: 'MEDIUM',
    notificationMethods: ['EMAIL'],
  });
  const [loading, setLoading] = useState(false);
  const [availableBirds, setAvailableBirds] = useState<Bird[]>([]);

  useEffect(() => {
    if (isOpen) {
      if (Array.isArray(birds) && birds.length > 0) {
        setAvailableBirds(birds);
      } else {
        loadBirds();
      }
    }
  }, [isOpen, birds]);

  const loadBirds = async () => {
    try {
      const response = await getBirds();
      setAvailableBirds(response);
    } catch (error) {
      console.error('Error loading birds:', error);
      setAvailableBirds([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.birdId || !formData.reminderDate || !formData.title) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await onSubmit({
        ...formData,
        reminderDate: new Date(formData.reminderDate).toISOString(),
      });
      handleClose();
    } catch (error) {
      console.error('Error scheduling health reminder:', error);
      alert('Failed to schedule health reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      birdId: '',
      reminderType: 'HEALTH_CHECKUP',
      reminderDate: '',
      frequency: 'ONCE',
      title: '',
      description: '',
      priority: 'MEDIUM',
      notificationMethods: ['EMAIL'],
    });
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNotificationMethodChange = (method: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      notificationMethods: checked 
        ? [...prev.notificationMethods, method]
        : prev.notificationMethods.filter(m => m !== method)
    }));
  };

  const selectedBird = Array.isArray(availableBirds) ? availableBirds.find(bird => bird.id === formData.birdId) : undefined;

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center">
            <BellIcon className="h-6 w-6 text-indigo-500 mr-3" />
            Schedule Health Reminder
          </span>
        }
        onClose={handleClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-6">
                  {/* Bird Selection */}
                  <div>
                    <label htmlFor="birdId" className="block text-sm font-medium text-slate-700 mb-2">
                      Select Bird *
                    </label>
                    <select
                      id="birdId"
                      name="birdId"
                      value={formData.birdId}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      required
                    >
                      <option key="empty-option-1753461297323" value="">Choose a bird...</option>
                      <option key="ALL_BIRDS-1753461297323" value="ALL_BIRDS" className="font-medium text-cyan-600">
                        🔵 All Birds / Flock
                      </option>
                      {Array.isArray(availableBirds) && availableBirds.map((bird) => (
                        <option key={bird.id} value={bird.id}>
                          {bird.bandId} - {bird.species?.commonName || 'Unknown Species'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Selected Bird Info */}
                  {selectedBird && (
                    <div className="bg-slate-50/80 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-900 mb-2">Selected Bird Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Band ID:</span>
                          <span className="ml-2 font-medium">{selectedBird.bandId}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Species:</span>
                          <span className="ml-2 font-medium">{selectedBird.species?.commonName || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Health Status:</span>
                          <span className="ml-2 font-medium">{selectedBird.healthStatus || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Last Checkup:</span>
                          <span className="ml-2 font-medium">
                            {selectedBird.lastHealthCheck 
                              ? new Date(selectedBird.lastHealthCheck).toLocaleDateString()
                              : 'No record'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Reminder Type */}
                  <div>
                    <label htmlFor="reminderType" className="block text-sm font-medium text-slate-700 mb-2">
                      Reminder Type *
                    </label>
                    <select
                      id="reminderType"
                      name="reminderType"
                      value={formData.reminderType}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      required
                    >
                      <option key="HEALTH_CHECKUP-1753461297323" value="HEALTH_CHECKUP">Health Checkup</option>
                      <option key="VACCINATION-1753461297323" value="VACCINATION">Vaccination</option>
                      <option key="MEDICATION-1753461297323" value="MEDICATION">Medication</option>
                      <option key="WEIGHT_CHECK-1753461297323" value="WEIGHT_CHECK">Weight Check</option>
                      <option key="BLOOD_TEST-1753461297323" value="BLOOD_TEST">Blood Test</option>
                      <option key="FECAL_EXAM-1753461297323" value="FECAL_EXAM">Fecal Examination</option>
                      <option key="GENERAL_OBSERVATION-1753461297323" value="GENERAL_OBSERVATION">General Observation</option>
                      <option key="CUSTOM-1753461297323" value="CUSTOM">Custom</option>
                    </select>
                  </div>

                  {/* Title */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-slate-700 mb-2">
                      Reminder Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="e.g., Annual Health Checkup"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-2">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Additional details about this reminder..."
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  {/* Reminder Date */}
                  <div>
                    <label htmlFor="reminderDate" className="block text-sm font-medium text-slate-700 mb-2">
                      Reminder Date *
                    </label>
                    <input
                      type="datetime-local"
                      id="reminderDate"
                      name="reminderDate"
                      value={formData.reminderDate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      required
                    />
                  </div>

                  {/* Frequency */}
                  <div>
                    <label htmlFor="frequency" className="block text-sm font-medium text-slate-700 mb-2">
                      Frequency
                    </label>
                    <select
                      id="frequency"
                      name="frequency"
                      value={formData.frequency}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="ONCE-1753461297323" value="ONCE">One Time</option>
                      <option key="DAILY-1753461297323" value="DAILY">Daily</option>
                      <option key="WEEKLY-1753461297323" value="WEEKLY">Weekly</option>
                      <option key="MONTHLY-1753461297323" value="MONTHLY">Monthly</option>
                      <option key="QUARTERLY-1753461297323" value="QUARTERLY">Quarterly</option>
                      <option key="YEARLY-1753461297323" value="YEARLY">Yearly</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-slate-700 mb-2">
                      Priority
                    </label>
                    <select
                      id="priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="LOW-1753461297323" value="LOW">Low</option>
                      <option key="MEDIUM-1753461297323" value="MEDIUM">Medium</option>
                      <option key="HIGH-1753461297323" value="HIGH">High</option>
                      <option key="URGENT-1753461297323" value="URGENT">Urgent</option>
                    </select>
                  </div>

                  {/* Notification Methods */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Notification Methods
                    </label>
                    <div className="space-y-2">
                      {['EMAIL', 'SMS', 'PUSH', 'IN_APP'].map((method) => (
                        <label key={method} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.notificationMethods.includes(method)}
                            onChange={(e) => handleNotificationMethodChange(method, e.target.checked)}
                            className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-slate-700">
                            {method.replace('_', ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Scheduling...' : 'Schedule Reminder'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default HealthRemindersModal; 